import { ChangeEvent, FC, Fragment, useEffect, useState } from "react";
import { Container } from "./style";
import Pagination from "components/particles/pagination/pagination";
import Button from "components/particles/primary-button";
import AddIngredientModal from "components/popus/ingredients-popup/add-ingredient";

import view from "assets/images/view-icon.svg";
import deleteIcon from "assets/images/delete.svg";
import NoDataFound from "components/particles/no-data-found";
import Swal from "sweetalert2";
import { useIngredient } from "./useHook";
import placeHolder from "assets/images/placeholder.jpg";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import { setBulkIngredientDetailsModalIsOpen, setBulkIngredientsModalIsOpen, setModalOpened } from "store/user-slice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes.enums";
import edit from "assets/images/edit.svg";
import {
  IngredientDTO,
  IngredientSearchDTO,
} from "utils/helpers/models/ingredients.dto";
import { useForm } from "react-hook-form";
import BulkIngredients from "components/popus/bulkIngredients-popup/BulkIngredients";
import BulkIngredientDetailsPopup from "components/popus/bulkIngredient-details/bulk-ingredient-details-popup";

const Ingredients: FC = () => {
  const isLoading = useSelector((state: any) => state.user.isLoading);
  const [ingredientPaginationState, setIngredientPaginationState] =
    useState<any>({});
  const [data, setData] = useState<any[]>([]);
  const [dataById, setDataById] = useState<any>();
  const navigate = useNavigate();
  const [id, setId] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState({
    name: "",
    status: "false",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const { addIngredientModalIsOpen, bulkIngredientsModalIsOpen, bulkIngredientDetailsModalIsOpen } = useSelector((state: any) => state.user);
  const {
    getIngredients,
    getIngredientById,
    deleteIngredient,
    putIngredient,
    searchIngredients,
  } = useIngredient();
  const [status, setStatus] = useState<string>();
  const handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setStatus(value);
    setValue("status", value);
  };

  const limit = 10;
  let tempData: any = [];
  const fetchNextRecords = (offset: number) => {
    const filters: any = {
      name: searchQuery?.name || "",
      status: status,
      offset: offset,
      limit: limit,
    };

    getIngredients(setData, filters, (paginationState: any) => {
      setIngredientPaginationState(paginationState);
      const toTopElement = document.getElementById("toTop");
      if (toTopElement) {
        toTopElement.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo(0, 0);
      }
    });
  };

  const addIngredient = (e: any) => {
    e.preventDefault();
    dispatch(setModalOpened("Ingredient"));
    setId(id);
    if (id !== 0) {
      getIngredientById(id, setDataById);
    }
  };

  const handleViewPage = (id: number) => {
    navigate(siteRoutes.ingredientDetail + `?${searchParams.toString()}`, {
      state: { id },
    });
  };

  const columns: any = ["Image", "Name", "Status", "Actions"];

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset: formReset,
  } = useForm<IngredientSearchDTO>();

  const refreshIngredientsList = async (offset?: number) => {
    const currentOffset =
      offset ?? ingredientPaginationState?.offset ?? 0;

    const filters: any = {
      name: searchQuery?.name || "",
      status: status,
      offset: currentOffset,
      limit,
    };

    const result = await getIngredients(
      setData,
      filters,
      setIngredientPaginationState
    );

    if (result?.ingredients?.length === 0 && currentOffset > 0) {
      const previousOffset = Math.max(0, currentOffset - limit);
      await getIngredients(
        setData,
        { ...filters, offset: previousOffset },
        setIngredientPaginationState
      );
    }
  };

  const handleDelete = (id: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteIngredient(id);
          await refreshIngredientsList();
          Swal.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
        } catch {
          Swal.fire({
            title: "Error",
            text: "Failed to delete ingredient. Please try again.",
            icon: "error",
          });
        }
      }
    });
  };

  useEffect(() => {
    getIngredients(setData, {}, setIngredientPaginationState);
  }, [AddIngredientModal]);

  const search = async (data: IngredientSearchDTO) => {
    const query = getValues();

    const parsedStatus =
      query.status === "true" ? true :
        query.status === "false" ? false :
          undefined;

    setSearchParams({
      name: query.name || "",
      status: query.status || "",
    });

    setSearchQuery({
      name: query.name || "",
      status: query.status || "",
    });

    await getIngredients(setData, {
      ...query,
      status: parsedStatus,
      offset: 0,
      limit,
    }, setIngredientPaginationState);
  };

  const reset = async () => {
    setSearchParams({});
    setValue("name", "");
    setValue("status", "");
    setSearchQuery({ name: "", status: "" });
    setStatus("");
    formReset();

    const resetPaginationState = { page: 1, total: 0, offset: 0 };
    setIngredientPaginationState(resetPaginationState);

    await getIngredients(setData, { offset: 0, limit }, (newPaginationState: any) => {
      setIngredientPaginationState({ ...newPaginationState, page: 1 });
    });
  };

  const toggleStatus = (ingredient: IngredientDTO, index: number) => {
    data[index] = ingredient;
    setData([...data]);
    putIngredient(ingredient?.id, ingredient);
  };

  const addBulkIngredients = (e: any) => {
    e.preventDefault();
    dispatch(setBulkIngredientsModalIsOpen());
  };

  const addBulkIngredientDetails = (e: any) => {
    e.preventDefault();
    dispatch(setBulkIngredientDetailsModalIsOpen());
  };

  useEffect(() => {
    const nameParam = searchParams.get("name") || "";
    const statusParam = searchParams.get("status");

    const parsedStatus =
      statusParam === "true" ? true :
        statusParam === "false" ? false :
          undefined;

    setValue("name", nameParam);
    setValue("status", statusParam || "");
    setSearchQuery({
      name: nameParam,
      status: statusParam || "",
    });
    setStatus(statusParam || "");

    getIngredients(setData, {
      name: nameParam,
      status: parsedStatus,
      offset: 0,
      limit: limit,
    }, setIngredientPaginationState);

  }, [location.key]);

  return (
    <Container>
      <div className="categories-title">
        <div>
          <h1 id="toTop">Ingredients</h1>
        </div>
        <div className="add-ingredient-button-container">
          <Button
            type="button"
            title="Add Ingredients"
            width="16.5rem"
            fill="true"
            backgroundcolor="var(--reset-button)"
            onclick={addIngredient}
          />
          <Button
            type="button"
            title="Add Bulk Ingredients"
            width="16.5rem"
            fill="true"
            backgroundcolor="var(--reset-button)"
            onclick={addBulkIngredients}
          />
          <Button
            type="button"
            title="Add Bulk Ingredient Details"
            width="16.5rem"
            fill="true"
            backgroundcolor="var(--reset-button)"
            onclick={addBulkIngredientDetails}
          />
        </div>
      </div>

      <div className="search-container">
        <div className="input-fields">
          <input
            type="text"
            placeholder="Search By Name"
            {...register("name")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit(search)();
              }
            }}
          />

          <select
            id="status"
            {...register("status")}
            value={status || ""}
            onChange={handleStatusChange}
          >
            <option value="">status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <Button
          title="Search"
          width="16rem"
          fill="true"
          onclick={handleSubmit(search)}
        />
        <Button
          title="Reset"
          width="16rem"
          fill="true"
          backgroundcolor="var(--reset-button)"
          onclick={handleSubmit(reset)}
        />
      </div>
      <div className="data-table">
        <table>
          <thead>
            <tr>
              {columns.map((column: any, index: number) => {
                return <th key={index}>{column}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {data?.length > 0 ? (
              data?.map((item: any, index: number) => {
                return (
                  <tr key={index}>
                    <td>
                      <img
                        src={
                          item?.image && !item?.image.includes(`${process.env.REACT_APP_BASE_URL}images/placeholder`)
                            ? item?.image
                            : placeHolder
                        }
                        alt="img"
                        width={60}
                        height={60}
                        style={{ borderRadius: "50%" }}
                      />
                    </td>
                    <td>{item.name}</td>
                    <td>
                      <label className="toggle-button">
                        <input
                          type="checkbox"
                          checked={item?.status}
                          onChange={(e) =>
                            toggleStatus(
                              { ...item, status: e.target.checked },
                              index
                            )
                          }
                        />
                        <span className="slider round"></span>
                      </label>
                    </td>
                    <td className="actions">
                      <div style={{ display: "flex", gap: "1rem" }}>
                        <div
                          className="view-container"
                          onClick={() => handleViewPage(item?.id)}
                        >
                          <img src={view} alt="img" />
                        </div>
                        <div
                          className="delete-container"
                          onClick={() => handleDelete(item?.id)}
                        >
                          <img src={deleteIcon} alt="img" />
                        </div>
                        <div className="edit-container" onClick={() => {
                          setId(item.id);
                          setDataById({
                            id: item.id,
                            name: item.name,
                            status: item.status,
                            image: item.image || "",
                          });
                          dispatch(setModalOpened("Ingredient"));
                        }}>
                          <img src={edit} alt="img" />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <>
                <tr>
                  <td colSpan={columns.length}>
                    <NoDataFound isShow={!isLoading} />
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      <Fragment>
        {/* FIX 3: Add resetKey to force pagination component re-render */}
        <Pagination
          key={`${ingredientPaginationState?.page}-${ingredientPaginationState?.total}`}
          totalRecords={ingredientPaginationState?.total || 0}
          perPage={limit}
          handleFetchNextRecords={fetchNextRecords}
          currentPage={ingredientPaginationState?.page}
        />
      </Fragment>

      {addIngredientModalIsOpen ? (
        <AddIngredientModal
          id={id}
          data={dataById}
          onSuccess={async () => {
            const name = searchParams.get("name") || "";
            const status = searchParams.get("status") || "";
            await search({
              name,
              status,
              dietaries: "",
              categories: "",
            });
          }}
        />
      ) : (
        ""
      )}

      {bulkIngredientsModalIsOpen ? (
        <BulkIngredients getIngredients={() => getIngredients(setData, {}, setIngredientPaginationState)} />
      ) : (
        ""
      )}

      {bulkIngredientDetailsModalIsOpen ? (
        <BulkIngredientDetailsPopup getIngredients={() => getIngredients(setData, {
          name: searchQuery?.name || "",
          status: status === "true" ? true : status === "false" ? false : undefined,
          offset: 0,
          limit: limit,
        }, setIngredientPaginationState)} />
      ) : (
        ""
      )}
    </Container>
  );
};

export default Ingredients;